import React, { useCallback, useEffect } from 'react';
import { Upload } from 'lucide-react';
import { Tooltip } from 'antd';
import { TextBtn } from '../../shared/ui/text-btn';
import { FlyoutPosition } from '../../shared/hooks/use-flyout-position';
import { useAudioServices } from '../../audio';
import { colors, dimensions, typography } from '../../theme';
import type { SoundPanelState } from '../hooks/use-sound-panel-state';
import { useAudioDurationProbe } from '../hooks/use-audio-duration-probe';
import { useApplyAudioClip } from '../hooks/use-apply-audio-clip';
import { SoundTabsNav } from './sound-tabs-nav';
import { VoiceoverTab } from './voiceover-tab';
import { MusicTab } from './music-tab';

const PANEL_WIDTH = 320;
const PANEL_SHADOW = '0 12px 32px rgba(15, 23, 42, 0.18)';

type SoundPanelContentProps = {
  pos: FlyoutPosition;
  state: SoundPanelState;
};

/**
 * Content shown inside the sound panel.
 * Handles loading, audio generation, and applying audio to the editor.
 */
export const SoundPanelContent: React.FC<SoundPanelContentProps> = ({ pos, state }) => {
  const { generateVoiceover, loadMusicTracks } = useAudioServices();
  const { activeTab, voiceover, music } = state;

  // Loads the music library the first time the Music tab is opened.
  const { tracks: musicTracksValue, isLoading: musicIsLoadingValue, setTracks, setIsLoading } = music;
  useEffect(() => {
    if (activeTab !== 'music' || musicTracksValue !== null || musicIsLoadingValue) return;
    setIsLoading(true);
    loadMusicTracks().then(tracks => {
      setTracks(tracks ?? []);
      setIsLoading(false);
    });
  }, [activeTab, musicTracksValue, musicIsLoadingValue, loadMusicTracks, setTracks, setIsLoading]);

  // Retrieves the duration of the generated voiceover.
  useAudioDurationProbe({
    url: voiceover.audioUrl,
    onDurationChange: voiceover.setAudioDurationMs
  });

  // Retrieves the duration of the selected music track.
  const selectedTrack = music.tracks?.find(t => t.id === music.selectedTrackId) ?? null;
  useAudioDurationProbe({
    url: selectedTrack?.url ?? null,
    knownDurationMs: selectedTrack?.durationMs ?? null,
    onDurationChange: music.setSelectedTrackDurationMs
  });

  const handleGenerate = useCallback(async () => {
    if (!voiceover.script.trim() || voiceover.isGenerating) return;
    voiceover.setIsGenerating(true);
    voiceover.setError(null);
    voiceover.setAudioUrl(null);
    const GENERIC_ERROR = 'Hubo un error al generar el audio. Intentá de nuevo.';
    try {
      const result = await generateVoiceover(voiceover.script);
      if (!result?.audioUrl) voiceover.setError(GENERIC_ERROR);
      else voiceover.setAudioUrl(result.audioUrl);
    } catch {
      voiceover.setError(GENERIC_ERROR);
    } finally {
      voiceover.setIsGenerating(false);
    }
  }, [voiceover, generateVoiceover]);

  // Clears the generated audio whenever the script changes.
  const handleScriptChange = useCallback(
    (next: string) => {
      voiceover.setScript(next);
      voiceover.setAudioUrl(null);
      voiceover.setError(null);
    },
    [voiceover]
  );

  const { handleApply, canApply, isApplyProbing } = useApplyAudioClip(state);

  return (
    <div
      data-panel-id='sound'
      style={{
        position: 'fixed',
        top: pos.top,
        right: pos.right,
        width: PANEL_WIDTH,
        background: colors.surfaceElevated,
        border: `1px solid ${colors.border}`,
        borderRadius: dimensions.radius.xl,
        padding: dimensions.spacing['3xl'],
        boxShadow: PANEL_SHADOW,
        fontFamily: typography.fontFamily.ui,
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        gap: dimensions.spacing['2xl'],
        maxHeight: 'calc(100vh - 64px)',
        overflowY: 'auto'
      }}
    >
      <h3
        style={{
          color: colors.textPrimary,
          fontSize: typography.fontSize.lg,
          fontWeight: typography.fontWeight.semibold,
          margin: 0
        }}
      >
        Panel de Sonido
      </h3>

      <Tooltip title='Próximamente' placement='top'>
        <span style={{ display: 'flex', width: '100%' }}>
          <TextBtn
            label='Subir archivo de audio'
            icon={<Upload size={dimensions.size.iconSm} />}
            variant='ghost'
            disabled
            fullWidth
            onClick={() => undefined}
          />
        </span>
      </Tooltip>

      <SoundTabsNav activeTab={activeTab} onChange={state.setActiveTab} />

      <div style={{ minHeight: 120 }}>
        {activeTab === 'voiceover' ? (
          <VoiceoverTab
            script={voiceover.script}
            onScriptChange={handleScriptChange}
            audioUrl={voiceover.audioUrl}
            isGenerating={voiceover.isGenerating}
            error={voiceover.error}
            volume={voiceover.volume}
            onVolumeChange={voiceover.setVolume}
            onGenerate={handleGenerate}
          />
        ) : (
          <MusicTab
            tracks={music.tracks}
            isLoading={music.isLoading}
            selectedTrackId={music.selectedTrackId}
            onSelectTrackId={music.setSelectedTrackId}
            volume={music.volume}
            onVolumeChange={music.setVolume}
          />
        )}
      </div>

      <TextBtn
        label='Aplicar al timeline'
        variant='primary'
        disabled={!canApply}
        loading={isApplyProbing}
        fullWidth
        onClick={handleApply}
      />
    </div>
  );
};
